import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, Mail, ScrollText, Quote, Stamp, Coins, Newspaper } from "lucide-react";
import {
  efFragmentsByEntity, efFragmentsByRelationship,
  efFragmentsBySource, efFragmentsByTimeline,
} from "../../lib/api";

/**
 * EvidenceFragments — public surfacing of the "exact email / page / excerpt"
 * fragments that anchor a graph object to a specific anchor inside its
 * parent source. Used inside entity pages, relationship cards, timeline
 * detail panels, and the source detail page.
 *
 * One of `entitySlug`, `relationshipId`, `sourceId`, or `timelineEventId`
 * is required.
 */

const TYPE_META = {
  email:                 { label: "email",            color: "#9FA8D4", Icon: Mail },
  document_excerpt:      { label: "document excerpt", color: "#CBB892", Icon: ScrollText },
  signature_page:        { label: "signature page",   color: "#E0A526", Icon: Stamp },
  payment_clause:        { label: "payment clause",   color: "#5DD4B3", Icon: Coins },
  timeline_reference:    { label: "timeline ref",     color: "#A39E93", Icon: FileText },
  news_quote:            { label: "news quote",       color: "#E4DFD3", Icon: Quote },
  press_release_excerpt: { label: "press release",    color: "#9FA8D4", Icon: Newspaper },
  court_excerpt:         { label: "court excerpt",    color: "#D92D20", Icon: ScrollText },
  other:                 { label: "fragment",         color: "#757065", Icon: FileText },
};

const fmtDate = (d) => (d ? d : null);

export const EvidenceFragments = ({
  entitySlug,
  relationshipId,
  sourceId,
  timelineEventId,
  title = "// EXACT EVIDENCE FRAGMENTS",
  emptyLabel = "No exact fragments attached yet.",
  testidPrefix = "ef-fragments",
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        let res = { fragments: [] };
        if (entitySlug)              res = await efFragmentsByEntity(entitySlug);
        else if (relationshipId)     res = await efFragmentsByRelationship(relationshipId);
        else if (sourceId)           res = await efFragmentsBySource(sourceId);
        else if (timelineEventId)    res = await efFragmentsByTimeline(timelineEventId);
        if (alive) setRows(res.fragments || []);
      } catch (_e) {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [entitySlug, relationshipId, sourceId, timelineEventId]);

  if (loading) {
    return (
      <section className="mt-8" data-testid={`${testidPrefix}-loading`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">{title}</div>
        <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
          Loading evidence fragments…
        </div>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="mt-8" data-testid={`${testidPrefix}-empty`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">{title}</div>
        <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
          {emptyLabel}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8" data-testid={testidPrefix}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3 flex items-center gap-2">
        <ScrollText size={12} /> {title}
        <span className="text-[#757065]">· {rows.length}</span>
      </div>
      {(() => {
        // Promote the strongest source-document fragment to a hero "primary
        // document excerpt" card. Heuristic (no schema change):
        //   1. fragment_type === "document_excerpt"
        //   2. quote_confidence === "verbatim"
        //   3. parent source is a primary / government record (DOJ, court,
        //      government, foia) — keeps news-reproduced fragments out of the
        //      anchor slot
        // First match wins. Email + news_quote + report fragments always stay
        // in the supporting grid.
        const PRIMARY_ORIGINS = new Set(["DOJ", "Government", "Court", "FOIA",
                                          "doj", "government", "court", "foia"]);
        const PRIMARY_TYPES = new Set(["doj_record", "doj_disclosure",
          "court_filing", "deposition", "government_record",
          "foia_document", "declassified_document", "press_release"]);
        const anchorIdx = rows.findIndex((f) =>
          f.fragment_type === "document_excerpt" &&
          f.quote_confidence === "verbatim" &&
          (PRIMARY_ORIGINS.has(f?.source?.source_origin) ||
           PRIMARY_TYPES.has(f?.source?.source_type) ||
           // Fallback for the Rothschild lane where the verbatim LoA is
           // reproduced by Anadolu but is still THE primary document of the
           // lane — accept any verbatim document_excerpt as anchor when no
           // government-hosted one exists.
           !rows.some((g) => g.fragment_type === "document_excerpt"
             && g.quote_confidence === "verbatim"
             && (PRIMARY_ORIGINS.has(g?.source?.source_origin)
               || PRIMARY_TYPES.has(g?.source?.source_type))))
        );
        const anchorFrag = anchorIdx >= 0 ? rows[anchorIdx] : null;
        const supportingRows = anchorFrag
          ? rows.filter((_, i) => i !== anchorIdx)
          : rows;

        const renderCard = (f, opts = {}) => {
          const meta = TYPE_META[f.fragment_type] || TYPE_META.other;
          const Icon = meta.Icon;
          const deepLink = f.url_override || f?.source?.url;
          const isHero = !!opts.hero;
          return (
            <div
              key={f.id}
              className={
                isHero
                  ? "border-2 border-[#D92D20] bg-[#0F0F0F] p-4 sm:p-5 hover:border-[#D92D20] transition-colors"
                  : "border border-[#2D2A26] bg-[#0F0F0F] p-3 hover:border-[#D92D20]/60 transition-colors"
              }
              data-testid={`${testidPrefix}-card-${f.id}`}
              data-hero={isHero ? "true" : "false"}
            >
              {isHero && (
                <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20] mb-2 flex items-center gap-2">
                  <Stamp size={11} /> // PRIMARY DOCUMENT EXCERPT · ANCHOR
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span
                  style={{ color: meta.color, borderColor: meta.color }}
                  className="font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 inline-flex items-center gap-1"
                >
                  <Icon size={9} /> {meta.label}
                </span>
                {/* Report-derived badge — email fragments whose parent source is a
                    news/investigative report are presented as report-derived, not
                    as DOJ-hosted primary documents. */}
                {f.fragment_type === "email" &&
                  ["investigative_report", "article", "media_report"].includes(f?.source?.source_type) && (
                    <span
                      title="Sourced via reporting that reproduces an email; not a standalone primary email document."
                      className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/40 px-1.5 py-0.5">
                      report-derived
                    </span>
                  )}
                {f.page_number && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] border border-[#454038] px-1.5 py-0.5">
                    {f.page_number}
                  </span>
                )}
                {f.quote_confidence === "verbatim" && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/40 px-1.5 py-0.5">
                    verbatim
                  </span>
                )}
                {f.document_date && (
                  <span className="font-mono text-[9px] text-[#757065] ml-auto">
                    {fmtDate(f.document_date)}
                  </span>
                )}
              </div>

              <div className={isHero ? "font-heading text-base sm:text-lg text-[#E4DFD3] mb-2" : "font-heading text-sm text-[#E4DFD3] mb-1"}>
                {f.title}
              </div>

              {/* Email metadata */}
              {(f.sender || f.recipient) && (
                <div className="font-mono text-[10px] text-[#A39E93] mb-2">
                  {f.sender && <><span className="text-[#757065]">from</span> {f.sender}</>}
                  {f.sender && f.recipient && <span className="text-[#757065]"> · </span>}
                  {f.recipient && <><span className="text-[#757065]">to</span> {f.recipient}</>}
                </div>
              )}

              {f.anchor_text && (
                <blockquote
                  className={
                    isHero
                      ? "border-l-2 border-[#D92D20] pl-3 italic text-sm text-[#CBB892] my-2 max-h-none"
                      : "border-l-2 border-[#D92D20]/60 pl-3 italic text-xs text-[#A39E93] my-2 max-h-32 overflow-y-auto"
                  }
                >
                  “{f.anchor_text}”
                </blockquote>
              )}

              {f.summary && (
                <div className={isHero ? "text-xs text-[#A39E93] mb-2" : "text-xs text-[#A39E93] mb-2 line-clamp-3"}>
                  {f.summary}
                </div>
              )}

              {f.source && (
                <div className="font-mono text-[10px] text-[#757065] mt-2 flex flex-wrap items-center gap-1">
                  <span>in</span>
                  <Link
                    to={`/epstein-files/source/${f.source.id}`}
                    className="text-[#A39E93] hover:text-[#D92D20] truncate max-w-[14rem]"
                  >
                    {f.source.title}
                  </Link>
                  {f.source.publisher && (
                    <span className="text-[#757065]">· {f.source.publisher}</span>
                  )}
                </div>
              )}

              {deepLink && (
                <a
                  href={deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] hover:text-[#E4DFD3]"
                  data-testid={`${testidPrefix}-open-${f.id}`}
                >
                  {f.url_override ? "Open at exact anchor" : "View document"} <ExternalLink size={9} />
                </a>
              )}

              {f.editor_note && (
                <div className="font-mono text-[9px] text-[#E0A526] mt-2 italic">
                  ⚠ {f.editor_note}
                </div>
              )}
            </div>
          );
        };

        return (
          <>
            {anchorFrag && (
              <div className="mb-4" data-testid={`${testidPrefix}-anchor`}>
                {renderCard(anchorFrag, { hero: true })}
              </div>
            )}
            {supportingRows.length > 0 && (
              <>
                {anchorFrag && (
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-2">
                    // SUPPORTING FRAGMENTS · {supportingRows.length}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-2">
                  {supportingRows.map((f) => renderCard(f))}
                </div>
              </>
            )}
          </>
        );
      })()}
    </section>
  );
};

export default EvidenceFragments;
